import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import Drawflow from 'drawflow';
import 'drawflow/dist/drawflow.min.css';
import { useAuth } from '../../context/AuthContext';
import { MdRefresh, MdOutlineDashboardCustomize, MdZoomIn, MdZoomOut, MdCenterFocusStrong } from 'react-icons/md';
import ExportDataModal from '../../components/ExportDataModal';
import ThresholdAlertModal from '../../components/company/ThresholdAlertModal';
import { useLanguage } from '../../context/LanguageContext';

// Veri kesintisi süresi firma bazındadır (admin > Bildirim Ayarları); firma yüklenince güncellenir.
const DEFAULT_STALE_MINUTES = 70;
let staleMs = DEFAULT_STALE_MINUTES * 60 * 1000;

const applyValueOrStale = (el, value, lastUpdated) => {
    const stale = !lastUpdated || (Date.now() - new Date(lastUpdated).getTime() > staleMs);
    if (stale) {
        el.innerHTML = '<span style="display:inline-block;width:38px;height:3px;background:var(--text-primary);border-radius:2px;vertical-align:middle;opacity:0.85;"></span>';
        el.style.setProperty('color', 'var(--text-primary)', 'important');
    } else {
        el.textContent = value;
        el.style.removeProperty('color');
    }
};

export default function CompanyDashboard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useLanguage();
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const [scadaReady, setScadaReady] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [thresholdAlerts, setThresholdAlerts] = useState([]);
    const [lastRefresh, setLastRefresh] = useState(null);

    const editorRef = useRef(null);
    const drawflowInstance = useRef(null);
    const timers = useRef([]);
    const autoFitRef = useRef(null);

    const ZOOM_MIN = 0.15;
    const ZOOM_MAX = 2.5;

    const handleZoomIn = () => {
        drawflowInstance.current?.zoom_in();
    };
    const handleZoomOut = () => {
        drawflowInstance.current?.zoom_out();
    };
    const handleZoomReset = () => {
        autoFitRef.current?.();
    };

    const applyResizes = (editor) => {
        const layoutData = editor.export();
        const nodes = Object.keys(layoutData.drawflow.Home.data).length ? layoutData.drawflow.Home.data : {};
        setTimeout(() => {
            for (let nodeId in nodes) {
                const node = nodes[nodeId];
                const el = document.getElementById(`node-${nodeId}`);
                if (el && node.data.width) {
                    const resizable = el.querySelector('.resizable-shape');
                    if (resizable) {
                        resizable.style.width = `${node.data.width}px`;
                        resizable.style.height = `${node.data.height}px`;
                        resizable.style.resize = 'none';
                    }
                }
                if (el && node.data.text !== undefined) {
                    const ta = el.querySelector('textarea');
                    if (ta) {
                        ta.value = node.data.text;
                        ta.readOnly = true;
                        ta.style.resize = 'none';
                    }
                }
                if (editor) {
                    try {
                        editor.updateConnectionNodes('node-' + nodeId);
                    } catch (e) { }
                }
            }
        }, 350);
    };

    const fetchLatestData = useCallback(async (compData) => {
        if (!compData || !compData.systems) return;
        const allSensors = compData.systems.flatMap(s => s.sensors || []);
        const sensorUuids = allSensors.map(s => s.sensorUUID);

        const allCounters = compData.systems.flatMap(s => s.counters || []);
        const counterUuids = allCounters.map(c => c.counterUUID);

        if (sensorUuids.length > 0) {
            try {
                const vRes = await api.post('/sensor-data/bulk/latest', { uuids: sensorUuids });
                if (vRes.data.success && Array.isArray(vRes.data.data)) {
                    vRes.data.data.forEach(item => {
                        document.querySelectorAll(`[data-sensor-uuid="${item.sensorUUID}"]`).forEach(el => {
                            applyValueOrStale(el, item.lastValue, item.lastUpdated);
                        });
                    });
                }
            } catch (err) {
                console.error("Canlı sensör verisi alınamadı:", err);
            }
        }

        if (counterUuids.length > 0) {
            try {
                const cRes = await api.post('/counter-data/bulk/latest', { uuids: counterUuids });
                if (cRes.data.success && Array.isArray(cRes.data.data)) {
                    cRes.data.data.forEach(item => {
                        document.querySelectorAll(`[data-counter-uuid="${item.counterUUID}"]`).forEach(el => {
                            applyValueOrStale(el, item.lastValue, item.lastUpdated);
                        });
                    });
                }
            } catch (err) {
                console.error("Canlı sayaç verisi alınamadı:", err);
            }
        }

        try {
            const sigRes = await api.get(`/status-signals/company/${compData._id}`);
            if (sigRes.data.success && Array.isArray(sigRes.data.data)) {
                sigRes.data.data.forEach(item => {
                    document.querySelectorAll(`.signal-indicator[data-signal-id="${item._id}"]`).forEach(el => {
                        const span = el.querySelector('span');
                        if (item.currentValue === 0) {
                            el.style.backgroundColor = 'var(--danger)';
                            el.style.boxShadow = '0 0 15px var(--danger)';
                            el.style.borderColor = 'rgba(255,255,255,0.2)';
                            if (span) span.textContent = '0';
                        } else if (item.currentValue === 1) {
                            el.style.backgroundColor = 'var(--success)';
                            el.style.boxShadow = '0 0 15px var(--success)';
                            el.style.borderColor = 'rgba(255,255,255,0.2)';
                            if (span) span.textContent = '1';
                        } else {
                            el.style.backgroundColor = 'var(--text-muted)';
                            el.style.boxShadow = '0 0 10px rgba(0,0,0,0.5)';
                            el.style.borderColor = 'rgba(255,255,255,0.1)';
                            if (span) span.textContent = '--';
                        }
                    });
                });
            }
        } catch (err) {
            console.error("Canlı durum sinyali verisi alınamadı:", err);
        }

        try {
            const plcRes = await api.get(`/plc/company/${compData._id}/remote-access`);
            if (plcRes.data.success && Array.isArray(plcRes.data.data)) {
                plcRes.data.data.forEach(plc => {
                    (plc.outputs || []).forEach(out => {
                        document.querySelectorAll(`.scada-output-toggle[data-output-id="${out._id}"]`).forEach(btn => {
                            const labelEl = btn.querySelector('.scada-toggle-label');
                            if (out.state) {
                                btn.classList.add('is-on');
                                if (labelEl) labelEl.textContent = 'AÇIK';
                            } else {
                                btn.classList.remove('is-on');
                                if (labelEl) labelEl.textContent = 'KAPALI';
                            }
                        });
                    });
                    (plc.referenceValues || []).forEach(ref => {
                        document.querySelectorAll(`.scada-ref-input[data-ref-id="${ref._id}"]`).forEach(inp => {
                            if (document.activeElement !== inp) inp.value = ref.value;
                        });
                    });
                });
            }
        } catch (err) {
            console.error("PLC verileri alınamadı:", err);
        }

        setLastRefresh(new Date());
    }, []);

    const checkThresholdAlerts = useCallback(async (companyId) => {
        try {
            const res = await api.get(`/sensors/company/${companyId}/threshold-alerts`);
            if (res.data.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
                setThresholdAlerts(res.data.data);
            }
        } catch (err) {
            console.error('Eşik uyarıları alınamadı:', err);
        }
    }, []);

    const fetchScadaAndData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/companies/${user.companyId}`);
            const comp = res.data.data;
            staleMs = (comp.alertSettings?.staleMinutes || DEFAULT_STALE_MINUTES) * 60 * 1000;
            setCompany(comp);
            setLoading(false);
            checkThresholdAlerts(comp._id);

            setTimeout(() => {
                if (!drawflowInstance.current && editorRef.current) {
                    const editor = new Drawflow(editorRef.current);
                    editor.reroute = true;
                    editor.editor_mode = 'fixed';
                    editor.zoom_max = ZOOM_MAX;
                    editor.zoom_min = ZOOM_MIN;

                    // Mouse wheel zoom (desktop)
                    editorRef.current.addEventListener('wheel', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!drawflowInstance.current) return;
                        const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
                        const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, drawflowInstance.current.zoom * factor));
                        drawflowInstance.current.zoom = next;
                        drawflowInstance.current.zoom_refresh();
                    }, { capture: true, passive: false });

                    // Two-finger pinch zoom (mobile/touch)
                    let pinchStartDist = null;
                    let pinchStartZoom = 1;
                    const touchDist = (touches) => {
                        const dx = touches[0].clientX - touches[1].clientX;
                        const dy = touches[0].clientY - touches[1].clientY;
                        return Math.sqrt(dx * dx + dy * dy);
                    };
                    editorRef.current.addEventListener('touchstart', (e) => {
                        if (e.touches.length === 2 && drawflowInstance.current) {
                            pinchStartDist = touchDist(e.touches);
                            pinchStartZoom = drawflowInstance.current.zoom;
                        }
                    }, { passive: true });
                    editorRef.current.addEventListener('touchmove', (e) => {
                        if (e.touches.length === 2 && pinchStartDist && drawflowInstance.current) {
                            e.preventDefault();
                            const ratio = touchDist(e.touches) / pinchStartDist;
                            const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, pinchStartZoom * ratio));
                            drawflowInstance.current.zoom = next;
                            drawflowInstance.current.zoom_refresh();
                        }
                    }, { passive: false });
                    editorRef.current.addEventListener('touchend', (e) => {
                        if (e.touches.length < 2) pinchStartDist = null;
                    }, { passive: true });

                    editor.start();
                    drawflowInstance.current = editor;

                    const precanvas = document.querySelector('.drawflow .parent-node');
                    if (precanvas) {
                        precanvas.style.pointerEvents = 'none';
                    }
                }

                if (comp.scadaLayout && comp.scadaLayout.drawflow) {
                    try {
                        drawflowInstance.current.import(comp.scadaLayout);
                        applyResizes(drawflowInstance.current);

                        const autoFitAndCenter = () => {
                            if (!drawflowInstance.current || !editorRef.current) return;
                            const domNodes = document.querySelectorAll('.drawflow-node');
                            if (domNodes.length === 0) return;

                            // 1) Zoom'u geçici olarak 1 yapalım. Boyut ve hat hesaplamaları distorsiyon olmadan en doğru şekilde yapılsın.
                            const originalZoom = drawflowInstance.current.zoom;
                            drawflowInstance.current.zoom = 1;
                            drawflowInstance.current.zoom_refresh();

                            // 2) Çizgilerin (hatların) bozulmaması için zoom 1 iken tüm bağlantıları çizelim
                            domNodes.forEach(n => {
                                try {
                                    const idStr = n.id.replace('node-', '');
                                    drawflowInstance.current.updateConnectionNodes('node-' + idStr);
                                } catch (e) { }
                            });

                            let minX = Infinity, minY = Infinity;
                            let maxX = -Infinity, maxY = -Infinity;

                            domNodes.forEach(node => {
                                const px = parseFloat(node.style.left || 0);
                                const py = parseFloat(node.style.top || 0);
                                const w = node.offsetWidth || 280;
                                const h = node.offsetHeight || 200;

                                if (px < minX) minX = px;
                                if (py < minY) minY = py;
                                if (px + w > maxX) maxX = px + w;
                                if (py + h > maxY) maxY = py + h;
                            });

                            if (minX === Infinity) {
                                drawflowInstance.current.zoom = originalZoom;
                                return;
                            }

                            const rawW = maxX - minX;
                            const rawH = maxY - minY;
                            const centerX = minX + (rawW / 2);
                            const centerY = minY + (rawH / 2);

                            const vpW = editorRef.current.clientWidth;
                            const vpH = editorRef.current.clientHeight;

                            // Her iki kenar için eşit mesafeyi garanti altına alan padding değerleri
                            const paddingX = 40;
                            const paddingY = 40;

                            const scaleX = (vpW - (paddingX * 2)) / (rawW || 1);
                            const scaleY = (vpH - (paddingY * 2)) / (rawH || 1);

                            // Scadanın ekrana tam oturması için minimum scale hesaplanır
                            let scale = Math.min(1, scaleX, scaleY);
                            if (scale < 0.1) scale = 0.1;

                            const precanvas = document.querySelector('.drawflow .parent-node') || document.querySelector('.drawflow .drawflow-node')?.parentElement;
                            if (precanvas) {
                                precanvas.style.transformOrigin = '0px 0px';
                            }

                            // Tam ortalamak için viewport merkezinden scada merkezini çıkartıyoruz
                            const transX = (vpW / 2) - (centerX * scale);
                            const transY = (vpH / 2) - (centerY * scale);

                            drawflowInstance.current.canvas_x = transX;
                            drawflowInstance.current.canvas_y = transY;
                            drawflowInstance.current.zoom = scale;
                            drawflowInstance.current.zoom_refresh();
                            
                            // DİKKAT: Burada veya sonrasında tekrar updateConnectionNodes ÇAĞRILMIYOR.
                            // Çizgiler scale olmadan çizildiği için zoom yapıldığında bozulma olmayacak.
                        };

                        autoFitRef.current = autoFitAndCenter;

                        setTimeout(() => {
                            autoFitAndCenter();
                            window.addEventListener('resize', autoFitAndCenter);
                            window.addEventListener('orientationchange', autoFitAndCenter);
                            fetchLatestData(comp);

                            setScadaReady(true);
                        }, 550);

                        window._scadaResizeListener = autoFitAndCenter;

                    } catch (e) { console.error("Import error", e); }

                } else {
                    toast(t('companyDashboard.notDesigned'), { icon: '⚠️' });
                }
            }, 100);

            const tVal = setInterval(() => fetchLatestData(comp), 7000);
            timers.current.push(tVal);

        } catch (err) {
            toast.error(t('companyDetail.dataLoadFailed'));
        } finally {
            setLoading(false);
        }
    }, [user, fetchLatestData, checkThresholdAlerts, t]);

    useEffect(() => {
        if (user && user.companyId) {
            fetchScadaAndData();
        }
        const currentTimers = timers.current;
        return () => {
            currentTimers.forEach(tVal => clearInterval(tVal));
            if (window._scadaResizeListener) {
                window.removeEventListener('resize', window._scadaResizeListener);
                window.removeEventListener('orientationchange', window._scadaResizeListener);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, fetchScadaAndData]);

    const handleTogglePlcOutput = async (plcId, outputId, btn) => {
        const isOn = btn.classList.contains('is-on');
        try {
            await api.put(`/plc/${plcId}/outputs/${outputId}`, { state: !isOn });
            const labelEl = btn.querySelector('.scada-toggle-label');
            if (!isOn) {
                btn.classList.add('is-on');
                if (labelEl) labelEl.textContent = 'AÇIK';
            } else {
                btn.classList.remove('is-on');
                if (labelEl) labelEl.textContent = 'KAPALI';
            }
            toast.success(t('plc.outputUpdated'));
        } catch (err) {
            toast.error(t('companyDetail.settingFailed'));
        }
    };

    const handleUpdatePlcRefValue = async (plcId, refId, newValue) => {
        if (!newValue?.trim()) {
            toast.error(t('plc.referenceValueVal') + ' zorunludur');
            return;
        }
        try {
            await api.put(`/plc/${plcId}/reference-values/${refId}`, { value: newValue.trim() });
            toast.success(t('plc.referenceValueUpdated'));
        } catch (err) {
            toast.error(t('companyDetail.settingFailed'));
        }
    };

    const handleCanvasClick = (e) => {
        const sensorItem = e.target.closest('.scada-sensor-item');
        if (sensorItem) {
            const sensorValSpan = sensorItem.querySelector('[data-sensor-uuid]');
            if (sensorValSpan) {
                const uuid = sensorValSpan.getAttribute('data-sensor-uuid');
                if (uuid) navigate(`/company/sensors/${uuid}`);
                return;
            }

            const counterValSpan = sensorItem.querySelector('[data-counter-uuid]');
            if (counterValSpan) {
                const uuid = counterValSpan.getAttribute('data-counter-uuid');
                if (uuid) navigate(`/company/counters/${uuid}`);
                return;
            }
        }

        const outputBtn = e.target.closest('.scada-output-toggle');
        if (outputBtn) {
            const plcId = outputBtn.getAttribute('data-plc-id');
            const outputId = outputBtn.getAttribute('data-output-id');
            handleTogglePlcOutput(plcId, outputId, outputBtn);
            return;
        }

        const refSaveBtn = e.target.closest('.scada-ref-save');
        if (refSaveBtn) {
            const plcId = refSaveBtn.getAttribute('data-plc-id');
            const refId = refSaveBtn.getAttribute('data-ref-id');
            const row = refSaveBtn.closest('.scada-ref-row');
            const input = row?.querySelector('.scada-ref-input');
            if (input) handleUpdatePlcRefValue(plcId, refId, input.value);
            return;
        }
    };

    const handleCanvasKeyDown = (e) => {
        if (e.key === 'Enter') {
            const input = e.target.closest('.scada-ref-input');
            if (input) {
                const plcId = input.getAttribute('data-plc-id');
                const refId = input.getAttribute('data-ref-id');
                handleUpdatePlcRefValue(plcId, refId, input.value);
            }
        }
    };

    if (loading && !company) {
        return <div className="loading-page"><div className="spinner"></div></div>;
    }

    return (
        <div style={{ height: 'calc(100vh - 64px)', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: 'var(--bg-body)' }}>
            {/* Minimal Dashboard Header */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap',
                padding: '16px 24px', background: 'var(--bg-card)', borderBottom: '1px solid var(--border)',
                zIndex: 10, gap: 12
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <div style={{ padding: 8, background: 'rgba(79, 142, 247, 0.15)', borderRadius: 8, color: 'var(--primary)', flexShrink: 0 }}>
                        <MdOutlineDashboardCustomize size={24} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{company?.name} {t('companyDashboard.scadaControlPanel')}</h1>
                        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 8 }}>
                            ● {t('companyDashboard.liveDataFlow')}
                            {lastRefresh && (
                                <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                                    {(() => {
                                        const d = lastRefresh;
                                        const pad = n => String(n).padStart(2, '0');
                                        return `${d.getFullYear()}.${pad(d.getMonth()+1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
                                    })()}
                                </span>
                            )}
                        </p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    {(user.isOwner || user.permissions?.canDownloadData) && (
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowExportModal(true)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                        <i className="fa fa-file-excel"></i> {t('companyDashboard.downloadAllData')}
                    </button>
                    )}
                    <button className="btn btn-secondary btn-sm" onClick={() => fetchScadaAndData()}>
                        <MdRefresh /> {t('common.refresh')}
                    </button>
                </div>
            </div>

            {/* SCADA Workspace */}
            <div
                style={{
                    flex: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundColor: 'var(--bg-body)',
                    backgroundImage: 'repeating-linear-gradient(rgba(128,128,128,0.1) 0, rgba(128,128,128,0.1) 1px, transparent 1px, transparent 40px), repeating-linear-gradient(90deg, rgba(128,128,128,0.1) 0, rgba(128,128,128,0.1) 1px, transparent 1px, transparent 40px)'
                }}
                onClick={handleCanvasClick}
                onKeyDown={handleCanvasKeyDown}
            >
                {/* Loading Overlay while readying layout */}
                {!scadaReady && (
                    <div style={{ position: 'absolute', inset: 0, background: 'var(--bg-body)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
                        <div className="spinner"></div>
                        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('companyDashboard.preparingScada')}</span>
                    </div>
                )}

                <div 
                    ref={editorRef} 
                    id="drawflow" 
                    style={{ 
                        width: '100%', 
                        height: '100%', 
                        opacity: scadaReady ? 1 : 0, 
                        transition: 'opacity 0.4s ease' 
                    }}
                ></div>

                {/* Zoom Controls */}
                <div className="scada-zoom-controls">
                    <button type="button" className="scada-zoom-btn" onClick={handleZoomIn} aria-label={t('common.zoomIn')}>
                        <MdZoomIn />
                    </button>
                    <button type="button" className="scada-zoom-btn" onClick={handleZoomOut} aria-label={t('common.zoomOut')}>
                        <MdZoomOut />
                    </button>
                    <button type="button" className="scada-zoom-btn" onClick={handleZoomReset} aria-label={t('common.fitToScreen')}>
                        <MdCenterFocusStrong />
                    </button>
                </div>
            </div>

            {/* Export Modal */}
            {showExportModal && (
                <ExportDataModal
                    companyId={user.companyId}
                    companyName={company?.name}
                    onClose={() => setShowExportModal(false)}
                />
            )}

            {/* Threshold Breach Alert */}
            {thresholdAlerts.length > 0 && (
                <ThresholdAlertModal
                    alerts={thresholdAlerts}
                    onClose={() => setThresholdAlerts([])}
                />
            )}

            {/* Drawflow Custom ReadOnly CSS Overrides */}

            <style dangerouslySetInnerHTML={{
                __html: `
                .drawflow {
                    background: transparent;
                }
                .drawflow .connection .main-path {
                    stroke: var(--primary) !important;
                    stroke-width: 5px !important;
                    stroke-dasharray: 10 10 !important;
                    animation: dashFlow 1s linear infinite !important;
                    filter: drop-shadow(0 0 4px var(--primary)) !important;
                    pointer-events: none;
                }
                .drawflow .connection .point {
                    stroke: var(--primary) !important;
                    stroke-width: 2px !important;
                    fill: var(--bg-card) !important;
                    pointer-events: none;
                    opacity: 0 !important; /* Görünmez yap / Hide connection dots */
                }
                @keyframes dashFlow {
                    from { stroke-dashoffset: 20; }
                    to { stroke-dashoffset: 0; }
                }

                .drawflow-node {
                    background: transparent !important;
                    border: none !important;
                    border-radius: 0 !important;
                    color: var(--text-primary) !important;
                    box-shadow: none !important;
                    padding: 0 !important;
                    cursor: default;
                    width: auto !important;
                }
                
                /* Sistem Node'u için Özel Stil (Dikdörtgen kutuyu geri getirir) */
                .drawflow-node.scada-system {
                    background: var(--bg-card) !important;
                    border: 1px solid var(--border) !important;
                    border-radius: 12px !important;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.15) !important;
                    min-width: 260px !important;
                }
                
                /* Kutusuz Metin (Text Node Without Box) */
                .drawflow-node.scada-text {
                    background: transparent !important;
                    border: none !important;
                    box-shadow: none !important;
                    min-width: unset !important;
                }

                /* Durum Sinyali Node'u */
                .drawflow-node.scada-status-signal {
                    background: transparent !important;
                    border: none !important;
                    box-shadow: none !important;
                }

                .drawflow_content_node {
                    padding: 0 !important;
                    margin: 0 !important;
                    display: block;
                    width: 100% !important;
                    box-sizing: border-box !important;
                }
                
                .drawflow {
                    /* prevent any pan/zoom/click on background */
                    touch-action: none;
                }
                .drawflow-node { 
                    pointer-events: auto !important;
                }
                .drawflow-node .inputs, .drawflow-node .outputs { 
                    pointer-events: none !important; 
                }
                .drawflow-node .input, .drawflow-node .output { 
                    pointer-events: none !important; 
                    opacity: 0 !important; /* Müşteri tarafında nokta görünmez */
                    width: 10px !important;
                    height: 3px !important;
                    border-radius: 0 !important;
                    border: 0 !important;
                    background: var(--primary) !important;
                }
                .drawflow-node .input {
                    left: 0px !important; /* Bağlantı hattı tam kutunun kenarında bitsin */
                }
                .drawflow-node .output {
                    right: 0px !important; /* Bağlantı hattı tam kutunun kenarında bitsin */
                }

                /* Prevent drawflow native delete/move */
                .drawflow .drawflow-node.selected {
                    border: 1px solid var(--border) !important;
                }
                .drawflow-delete { display: none !important; }

                .scada-node-card { padding: 0; min-width: 100%; box-sizing: border-box; background: transparent; }
                .scada-node-header {
                    background: rgba(79, 142, 247, 0.1) !important;
                    padding: 12px 16px !important;
                    border-bottom: 1px solid var(--border) !important;
                    border-radius: 12px 12px 0 0 !important;
                    font-weight: 700;
                    font-size: 15px;
                    color: var(--primary) !important;
                    display: flex; gap: 8px; align-items: center;
                    width: 100%;
                    box-sizing: border-box;
                }
                .scada-node-body {
                    padding: 12px !important;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    background: transparent !important;
                    width: 100%;
                    box-sizing: border-box;
                }
                .scada-sensor-item {
                    display: flex; justify-content: space-between; align-items: center;
                    background: var(--bg-body) !important;
                    padding: 8px 12px;
                    border-radius: 6px;
                    font-size: 12px;
                    cursor: pointer;
                    transition: all 0.2s;
                    border: 1px solid var(--border) !important;
                    color: var(--text-primary) !important;
                    pointer-events: auto !important; /* Re-enable clicks so navigation works */
                }
                .scada-sensor-item:hover {
                    background: var(--bg-card) !important;
                    border-color: var(--primary) !important;
                }
                .scada-sensor-item .sensor-name { font-size: 14px; font-weight: 600; pointer-events: none; }
                .scada-sensor-item .sensor-val { font-size: 18px; font-weight: 800; color: var(--success) !important; font-family: monospace; pointer-events: none; }
                .scada-sensor-item .sensor-unit { font-size: 10px; color: var(--text-muted) !important; pointer-events: none; }
                
                .scada-icon-wrapper { padding: 10px; display: flex; align-items: center; justify-content: center; }

                .scada-text-node textarea {
                    background: transparent;
                    border: none;
                    color: var(--text-primary) !important;
                    font-family: inherit;
                    font-size: 16px;
                    font-weight: 600;
                    pointer-events: none;
                }

                .drawflow-node.scada-plc-output {
                    background: var(--bg-card) !important;
                    border: 1px solid var(--border) !important;
                    border-radius: 10px !important;
                    min-width: 150px !important;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
                }
                .drawflow-node.scada-plc-refvalue {
                    background: var(--bg-card) !important;
                    border: 1px solid var(--border) !important;
                    border-radius: 10px !important;
                    min-width: 200px !important;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
                }
                .scada-plc-outp { padding: 12px; box-sizing: border-box; }
                .scada-plc-node-label {
                    font-size: 13px; font-weight: 600; color: var(--text-primary);
                    margin-bottom: 10px; text-align: center;
                }
                .scada-output-toggle {
                    display: flex; align-items: center; gap: 8px;
                    background: none; border: 1px solid var(--border); border-radius: 20px;
                    padding: 6px 10px; cursor: pointer; width: 100%; justify-content: center;
                    transition: border-color 0.2s; pointer-events: auto !important;
                }
                .scada-output-toggle.is-on {
                    border-color: var(--success); background: rgba(34, 197, 94, 0.08);
                }
                .scada-toggle-track {
                    position: relative; width: 36px; height: 20px; border-radius: 10px;
                    background: var(--text-muted); flex-shrink: 0; transition: background 0.25s;
                }
                .scada-output-toggle.is-on .scada-toggle-track { background: var(--success); }
                .scada-toggle-thumb {
                    position: absolute; top: 3px; left: 3px; width: 14px; height: 14px;
                    border-radius: 50%; background: white; transition: left 0.25s;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                }
                .scada-output-toggle.is-on .scada-toggle-thumb { left: 19px; }
                .scada-toggle-label { font-size: 12px; font-weight: 700; color: var(--text-muted); }
                .scada-output-toggle.is-on .scada-toggle-label { color: var(--success); }
                .scada-ref-row { display: flex; align-items: center; gap: 6px; }
                .scada-ref-input {
                    flex: 1; background: var(--bg-body); border: 1px solid var(--border);
                    border-radius: 6px; color: var(--primary); font-family: monospace;
                    font-size: 13px; font-weight: 700; padding: 5px 8px; text-align: right;
                    min-width: 0; box-sizing: border-box; pointer-events: auto !important;
                }
                .scada-ref-save {
                    flex-shrink: 0; background: var(--primary); border: none; border-radius: 6px;
                    color: white; font-size: 13px; font-weight: 700; padding: 5px 10px;
                    cursor: pointer; pointer-events: auto !important;
                }

                ::-webkit-scrollbar {
                    width: 6px; height: 6px;
                }

                .scada-zoom-controls {
                    position: absolute;
                    right: 20px;
                    bottom: 20px;
                    z-index: 20;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .scada-zoom-btn {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    border: 1px solid var(--border);
                    background: var(--bg-card);
                    color: var(--text-primary);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    cursor: pointer;
                    box-shadow: var(--shadow-md);
                    transition: background 0.2s, border-color 0.2s;
                }
                .scada-zoom-btn:hover {
                    background: var(--bg-card-hover);
                    border-color: var(--border-hover);
                }
                @media (max-width: 480px) {
                    .scada-zoom-controls {
                        right: 12px;
                        bottom: 12px;
                    }
                    .scada-zoom-btn {
                        width: 40px;
                        height: 40px;
                        font-size: 18px;
                    }
                }
            `}} />
        </div>
    );
}
